import { type FC } from 'react'

import { useIntercom } from 'react-use-intercom'

import aiToolsIcon from '@/assets/icons/black-friday/ai-tools.webp'
import background from '@/assets/icons/black-friday/background.png'
import communityIcon from '@/assets/icons/black-friday/community.webp'
import eBookIcon from '@/assets/icons/black-friday/e-book.webp'
import liveCallIcon from '@/assets/icons/black-friday/live-call.webp'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import type { DialogProps } from '@/dialogs/types'

const bonuses = [
  {
    icon: eBookIcon,
    title: 'CreatorHero Collection - Ebooks & Guides',
  },
  {
    icon: communityIcon,
    title: 'Live Instagram SOP Call + Q&A',
  },
  { icon: liveCallIcon, title: 'Access to Supercharged 2.0 Community' },
  {
    icon: aiToolsIcon,
    title: 'Early access to AI tools & premium features',
  },
]

export const BlackFridayPromotionDialog: FC<DialogProps> = ({
  isOpen,
  onClose,
}) => {
  const intercom = useIntercom()

  const handleClickNewLicense = () => {
    intercom.showNewMessage("I'd like to get a license.")
    onClose()
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="max-h-[calc(100vh-32px)] max-[390px]:max-h-[calc(100vh-16px)] flex flex-col lg:max-w-[634px] bg-[#0A0C16] sm:max-w-[634px] sm:p-9 p-6 rounded-2xl"
      closeIconClassName="p-2 z-20 rounded-full bg-white/10 [&_svg]:text-white [&_svg]:h-6 [&_svg]:w-6 h-10 w-10"
      containerClassName="items-center max-[390px]:p-2"
      childrenClassName="max-h-full overflow-y-hidden flex flex-col"
    >
      <div className="relative z-10 max-h-[457px] h-auto shrink max-[390px]:h-[120px] flex items-center justify-center overflow-hidden">
        <img
          src={background}
          alt="Black Friday promotion background"
          className="w-full h-full relative object-cover"
        />
        <div className="absolute z-10 shadow-[0_0_40px_20px_#0A0C16_inset] inset-0" />
      </div>
      <div className="z-10 relative min-[390px]:-mt-14 flex flex-col items-center shrink-0">
        <h2 className="text-center text-xl sm:text-[32px] sm:leading-10 font-semibold text-white">
          Get 3 Months for the Price of 2
        </h2>
        <p className="bg-[linear-gradient(90deg,#3A24D3_3.56%,#6848D4_99.91%)] bg-clip-text text-transparent text-xl font-semibold mt-2 sm:mt-4">
          Plus Exclusive Bonuses:
        </p>
        <div className="mt-4 sm:grid sm:grid-cols-2 sm:grid-rows-2 flex flex-col gap-4 bg-[linear-gradient(0deg,rgba(24,57,156,0.20)_-6.22%,rgba(5,10,29,0.00)_76.49%)] bg-[rgba(1,4,22,0.50)] w-full p-4 sm:p-6 rounded-[20px] border border-[#1E2D50] shadow-[0_0_25px_0rgba(20,36,73,0.50)_inset]">
          {bonuses.map(({ icon, title }) => (
            <div
              className="flex items-center gap-4"
              key={title}
            >
              <img
                className="h-10 w-10"
                src={icon}
                alt={title}
              />
              <p className="text-sm font-medium text-white">{title}</p>
            </div>
          ))}
        </div>
      </div>
      <Button
        className="w-full mt-8 rounded-full bg-[#0057FF] hover:bg-[#0057FF]/90 font-semibold shrink-0"
        onClick={handleClickNewLicense}
      >
        Add New License
      </Button>
      <p className="mt-4 text-xs sm:text-sm leading-[22px] text-secondary-300 text-center">
        This offer applies <b>only to new creators</b> added during the
        campaign.
        <br className="hidden sm:block" />
        Existing licenses are excluded.
      </p>
    </Dialog>
  )
}
